import type {
  GetSelfApiV1UserSelfGetApiResponse,
  UpdateWorkdayUserRequest,
  WorkdayEnvironmentNode,
} from '~/api/rolodex/typedefs.autogen';

import { endpoints as autogenEnhancements } from '~/api/rolodex/enhancements.autogen';
import { Tags } from '~/api/rolodex/tags.autogen';

import { rolodexApiRoot } from './root';

type WorkdayEnvironments = Record<string, WorkdayEnvironmentNode>;

type WorkdayTenantEnvironmentParams = {
  tenantId: string;
  tenantEnvironment: string;
  cpsHost?: string | null;
  galahadHost?: string | null;
  suvId?: string | null;
};

type RefreshWorkdayClientHostsParams = {
  pilotClientId: number;
  cpsHost?: string | null;
  galahadHost?: string | null;
  suvId?: string | null;
};

type RefreshWorkdayClientRegistrationParams = {
  pilotClientId: number;
};

type RefreshWorkdayClientRegistrationResponse = {
  detail: string;
};

type UpdateWorkdayUserParams = {
  userId: string | number;
  body: {
    roleUuid: string;
    pilotDepartmentIds: number[];
  };
};

type CurrentUserAttributes = {
  user_management: 'workday' | 'evisort';
};

type CurrentUserResponse = {
  data: Array<{ attributes: CurrentUserAttributes }>;
};

function mapWorkdayEnvironments(
  environments: WorkdayEnvironmentNode[],
): WorkdayEnvironments {
  return environments.reduce<WorkdayEnvironments>((result, environment) => {
    result[environment.environment] = environment;
    return result;
  }, {});
}

rolodexApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });

export const rolodexApi = rolodexApiRoot.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    getWorkdayEnvironments: build.query<WorkdayEnvironments, void>({
      query: () => ({
        url: '/api/v1/client-mapping/workday/environments',
        method: 'get',
      }),
      transformResponse: (response: WorkdayEnvironmentNode[]) =>
        mapWorkdayEnvironments(response),
      providesTags: [{ type: Tags.client_mapping, id: 'workday-environments' }],
    }),

    getWorkdayTenantEnvironment: build.query<
      WorkdayEnvironmentNode,
      WorkdayTenantEnvironmentParams
    >({
      query: ({
        tenantId,
        tenantEnvironment,
        cpsHost,
        galahadHost,
        suvId,
      }) => ({
        url: `/api/v1/client-mapping/workday/environments/${encodeURIComponent(tenantId)}/${encodeURIComponent(tenantEnvironment)}`,
        method: 'get',
        params: {
          cps_host: cpsHost,
          galahad_host: galahadHost,
          suv_id: suvId,
        },
      }),
    }),

    refreshWorkdayClientHosts: build.mutation<
      WorkdayEnvironmentNode,
      RefreshWorkdayClientHostsParams
    >({
      query: ({ pilotClientId, cpsHost, galahadHost, suvId }) => ({
        url: `/api/v1/client-mapping/${pilotClientId}/workday/refresh-hosts`,
        method: 'post',
        body: {},
        params: {
          cps_host: cpsHost,
          galahad_host: galahadHost,
          suv_id: suvId,
        },
      }),
      invalidatesTags: [
        { type: Tags.client_mapping, id: 'workday-environments' },
      ],
    }),

    refreshWorkdayClientRegistration: build.mutation<
      RefreshWorkdayClientRegistrationResponse,
      RefreshWorkdayClientRegistrationParams
    >({
      query: ({ pilotClientId }) => ({
        url: `/api/v1/client-mapping/${pilotClientId}/workday/refresh-registration`,
        method: 'post',
        body: {},
      }),
    }),

    updateWorkdayUser: build.mutation<void, UpdateWorkdayUserParams>({
      query: ({ userId, body }) => ({
        url: `/api/v1/user/workday/${userId}`,
        method: 'put',
        body: {
          role_uuid: body.roleUuid,
          pilot_department_ids: body.pilotDepartmentIds,
        } satisfies UpdateWorkdayUserRequest,
      }),
      invalidatesTags: [{ type: Tags.user, id: 'LIST' }],
    }),

    getIsWorkdayUser: build.query<boolean, void>({
      query: () => ({ url: '/api/v1/user/self', method: 'get' }),
      transformResponse: (response: GetSelfApiV1UserSelfGetApiResponse) => {
        const currentUser = response as unknown as CurrentUserResponse;
        return currentUser.data[0]?.attributes.user_management === 'workday';
      },
      providesTags: [{ type: Tags.user, id: 'self' }],
    }),
  }),
});
